/**
 * Canonical slide-level classification annotation UI config.
 * Draft editing stays in ConfigForm; normalize at runtime and publish boundaries.
 */

import { normalizeCompletionPolicy } from '../../completionPolicy';
import {
    DEFAULT_ITEM_METADATA,
    normalizeItemMetadataConfig,
    prepareItemMetadataForAnnotationUISave,
    validateItemMetadataConfig,
} from '../../itemMetadata/itemMetadataModel';

/** Reserved category id for free-text answers (persisted + dropdown option when hybrid). */
export const SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID = '__freeText__';

function toTrimmedString(v) {
    return typeof v === 'string' ? v.trim() : String(v ?? '').trim();
}

/**
 * @param {unknown} c
 * @param {number} index
 */
function normalizeOneCategory(c) {
    const idRaw = c?.id;
    const id = typeof idRaw === 'string' ? idRaw.trim() : String(idRaw ?? '').trim();
    const labelRaw = c?.label;
    const label = typeof labelRaw === 'string' ? labelRaw.trim() : String(labelRaw ?? '').trim();
    return {
        id,
        label: label || id,
    };
}

/** Default dropdown label for hybrid free-text option when config label is empty/whitespace-only. */
export const DEFAULT_FREE_TEXT_OPTION_LABEL = 'Other (free text)';

/** Default ViewerTopBar task section label for slide-level questions UI. */
export const DEFAULT_TASK_SECTION_LABEL = 'Questions:';

const EMPTY_ANSWER = {
    categoryId: null,
    categoryLabel: null,
    freeText: null,
};

/**
 * Runtime / display label for the free-text dropdown option. Preserves admin draft spacing;
 * only whitespace-only strings fall back to {@link DEFAULT_FREE_TEXT_OPTION_LABEL}.
 *
 * @param {unknown} optionLabel - raw config `freeText.optionLabel` (draft-safe from normalize)
 */
export function getFreeTextOptionLabelForDisplay(optionLabel) {
    const s = optionLabel == null ? '' : String(optionLabel);
    return s.trim() === '' ? DEFAULT_FREE_TEXT_OPTION_LABEL : s;
}

/**
 * Trim free-text config strings at project save (placeholder, option label).
 * @param {object} question
 */
function trimQuestionFreeTextForPersist(question) {
    if (!question || typeof question !== 'object') return question;
    const ft = question.freeText;
    if (!ft || typeof ft !== 'object') return question;
    return {
        ...question,
        freeText: {
            ...ft,
            placeholder: toTrimmedString(ft.placeholder == null ? '' : String(ft.placeholder)),
            optionLabel: (() => {
                const t = toTrimmedString(ft.optionLabel == null ? '' : String(ft.optionLabel));
                return t || DEFAULT_FREE_TEXT_OPTION_LABEL;
            })(),
        },
    };
}

/**
 * Persisted config: questions[] only (no legacy top-level answer fields).
 * @param {object} config - normalized
 */
function stripCompatFieldsForPersist(config) {
    if (!config || typeof config !== 'object') return config;
    const questions = Array.isArray(config.questions)
        ? config.questions.map((q) => trimQuestionFreeTextForPersist(q))
        : [];
    const {
        categories: _c,
        allowDiscreteCategories: _d,
        allowFreeText: _f,
        freeText: _ft,
        ...rest
    } = config;
    return {
        ...rest,
        taskSectionLabel: toTrimmedString(rest.taskSectionLabel ?? DEFAULT_TASK_SECTION_LABEL)
            || DEFAULT_TASK_SECTION_LABEL,
        singleQuestionControlLabel: toTrimmedString(rest.singleQuestionControlLabel ?? ''),
        questions,
    };
}

/**
 * Apply canonical transforms to annotationUI immediately before PUT projects/:id.
 *
 * @param {{ id?: string, config?: object }} annotationUI
 */
export function prepareAnnotationUIForProjectSave(annotationUI) {
    const withMeta = prepareItemMetadataForAnnotationUISave(annotationUI);
    const ann = withMeta && typeof withMeta === 'object' ? withMeta : {};
    if (ann.id !== 'slide-level-classification') return withMeta;
    const cfg = ann.config && typeof ann.config === 'object' ? ann.config : {};
    const normalized = normalizeSlideLevelClassificationConfig(cfg);
    return {
        ...ann,
        config: stripCompatFieldsForPersist(normalized),
    };
}

/**
 * Whether the annotator has a complete answer for strict completion checks.
 * @param {object} params
 * @param {boolean} params.allowDiscreteCategories
 * @param {boolean} params.allowFreeText
 * @param {{ id: string }[]} params.categories
 * @param {string} params.categoryId
 * @param {string} params.freeTextAnswer
 */
export function isSlideLevelClassificationAnswerComplete({
    allowDiscreteCategories,
    allowFreeText,
    categories,
    categoryId,
    freeTextAnswer,
}) {
    const id = categoryId != null ? String(categoryId).trim() : '';
    const ftTrim = String(freeTextAnswer ?? '').trim();
    const ftPath = id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID;

    if (allowDiscreteCategories && !allowFreeText) {
        return (
            id !== ''
            && id !== SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID
            && categories.some((c) => String(c.id) === id)
        );
    }

    if (!allowDiscreteCategories && allowFreeText) {
        return ftTrim.length > 0;
    }

    if (allowDiscreteCategories && allowFreeText) {
        if (ftPath) return ftTrim.length > 0;
        return id !== '' && categories.some((c) => String(c.id) === id);
    }

    return false;
}

export const DEFAULT_SLIDE_LEVEL_QUESTION = {
    id: 'q1',
    label: '',
    categories: [],
    allowDiscreteCategories: true,
    allowFreeText: false,
    freeText: {
        multiline: false,
        placeholder: '',
        optionLabel: DEFAULT_FREE_TEXT_OPTION_LABEL,
    },
};

export const DEFAULT_SLIDE_LEVEL_CLASSIFICATION_CONFIG = {
    instructions: '',
    taskSectionLabel: DEFAULT_TASK_SECTION_LABEL,
    singleQuestionControlLabel: '',
    questions: [{ ...DEFAULT_SLIDE_LEVEL_QUESTION }],
    itemMetadata: { ...DEFAULT_ITEM_METADATA },
    itemMetadataDisplayKeys: [],
    columnLabels: {},
};

/**
 * @param {unknown} q
 * @param {number} [index] - used when id is missing
 */
export function normalizeOneQuestion(q, index = 0) {
    const raw = q && typeof q === 'object' ? q : {};
    const idRaw = raw.id;
    let id = typeof idRaw === 'string' ? idRaw.trim() : String(idRaw ?? '').trim();
    if (!id) {
        id = `q${index + 1}`;
    }
    const labelRaw = raw.label;
    const label = labelRaw == null ? '' : String(labelRaw);
    const categoriesRaw = Array.isArray(raw.categories) ? raw.categories : [];
    const categories = categoriesRaw.map((c) => normalizeOneCategory(c));
    const modes = normalizeAnswerModes(raw);
    return {
        id,
        label,
        categories,
        allowDiscreteCategories: modes.allowDiscreteCategories,
        allowFreeText: modes.allowFreeText,
        freeText: modes.freeText,
    };
}

/**
 * @param {string[]} [existingIds]
 */
export function createDefaultQuestion(existingIds = []) {
    const used = new Set(
        existingIds.map((id) => toTrimmedString(String(id))).filter(Boolean),
    );
    let n = 1;
    let id = `q${n}`;
    while (used.has(id)) {
        n += 1;
        id = `q${n}`;
    }
    return {
        ...DEFAULT_SLIDE_LEVEL_QUESTION,
        id,
        categories: [],
    };
}

/**
 * @param {object} raw - draft or persisted config
 * @returns {object[]}
 */
function migrateQuestionsFromRaw(raw) {
    const qRaw = Array.isArray(raw.questions) ? raw.questions : null;
    if (qRaw && qRaw.length > 0) {
        return qRaw.map((q, i) => normalizeOneQuestion(q, i));
    }
    const modes = normalizeAnswerModes(raw);
    const categoriesRaw = Array.isArray(raw.categories) ? raw.categories : [];
    return [
        normalizeOneQuestion({
            id: 'q1',
            label: '',
            categories: categoriesRaw,
            allowDiscreteCategories: modes.allowDiscreteCategories,
            allowFreeText: modes.allowFreeText,
            freeText: modes.freeText,
        }, 0),
    ];
}

/**
 * Runtime compat: mirror first question onto top-level fields for single-question annotator UI.
 * @param {object} out - normalized config object to mutate
 * @param {object|undefined} primary
 */
function applyPrimaryQuestionCompat(out, primary) {
    if (!primary) return;
    out.categories = primary.categories;
    out.allowDiscreteCategories = primary.allowDiscreteCategories;
    out.allowFreeText = primary.allowFreeText;
    out.freeText = primary.freeText;
}

/**
 * @param {object} [config]
 * @returns {object[]}
 */
export function getSlideLevelClassificationQuestions(config) {
    return normalizeSlideLevelClassificationConfig(config).questions;
}

/**
 * Display name for a question in viewer UI and validation messages.
 * @param {object} q
 * @param {number} qIndex
 */
export function getQuestionDisplayName(q, qIndex) {
    const label = toTrimmedString(q?.label ?? '');
    if (label) return label;
    return `Question ${qIndex + 1}`;
}

/**
 * Strip trailing colon for panel trigger prefix (e.g. "Questions:" → "Questions").
 * @param {string} taskSectionLabel
 */
export function getTaskSectionLabelPrefix(taskSectionLabel) {
    const s = toTrimmedString(taskSectionLabel ?? DEFAULT_TASK_SECTION_LABEL);
    return s.replace(/:+\s*$/, '') || 'Questions';
}

/**
 * ViewerTopBar task section label: config → registry fallback → default.
 * @param {object} [uiConfig]
 * @param {string} [registryLabel]
 */
export function getSlcTaskSectionLabel(uiConfig, registryLabel) {
    const normalized = normalizeSlideLevelClassificationConfig(uiConfig);
    const fromConfig = toTrimmedString(normalized.taskSectionLabel);
    if (fromConfig) return fromConfig;
    const fromRegistry = toTrimmedString(registryLabel ?? '');
    if (fromRegistry) return fromRegistry;
    return DEFAULT_TASK_SECTION_LABEL;
}

/**
 * @param {unknown} raw
 * @returns {{ categoryId: string|null, categoryLabel: string|null, freeText: string|null }}
 */
export function normalizeStoredAnswer(raw) {
    if (!raw || typeof raw !== 'object') {
        return { ...EMPTY_ANSWER };
    }
    const cid = raw.categoryId;
    const categoryId = cid == null || String(cid).trim() === '' ? null : String(cid).trim();
    const cl = raw.categoryLabel;
    const categoryLabel = cl == null || String(cl).trim() === '' ? null : String(cl);
    const ft = raw.freeText;
    const freeText = ft == null || String(ft).trim() === '' ? null : String(ft).trim();
    return { categoryId, categoryLabel, freeText };
}

/**
 * Load answers map from exportData, migrating legacy flat slideClassification when needed.
 * @param {object|undefined} exportData
 * @param {object[]} questions - normalized questions
 */
export function migrateAnswersFromExportData(exportData, questions) {
    const sc = exportData?.slideClassification;
    const out = {};
    questions.forEach((q) => {
        out[q.id] = { ...EMPTY_ANSWER };
    });

    if (!sc || typeof sc !== 'object') {
        return out;
    }

    const answersRaw = sc.answers;
    if (answersRaw && typeof answersRaw === 'object' && !Array.isArray(answersRaw)) {
        questions.forEach((q) => {
            if (Object.prototype.hasOwnProperty.call(answersRaw, q.id)) {
                out[q.id] = normalizeStoredAnswer(answersRaw[q.id]);
            }
        });
        return out;
    }

    const primaryId = questions[0]?.id ?? 'q1';
    if (
        sc.categoryId != null
        || sc.categoryLabel != null
        || (sc.freeText != null && String(sc.freeText).trim() !== '')
    ) {
        out[primaryId] = normalizeStoredAnswer(sc);
    }
    return out;
}

/**
 * Build one answer entry for persist.
 * @param {object} params
 */
export function buildStoredAnswerPayload({
    allowDiscreteCategories,
    allowFreeText,
    categoryId,
    categoryLabel,
    freeTextAnswer,
    freeTextOptionLabel,
}) {
    const id = categoryId != null ? String(categoryId).trim() : '';
    const ftTrim = String(freeTextAnswer ?? '').trim();
    const label = categoryLabel != null ? String(categoryLabel) : '';

    if (!allowDiscreteCategories && allowFreeText) {
        if (!ftTrim) return { ...EMPTY_ANSWER };
        return {
            categoryId: SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID,
            categoryLabel: freeTextOptionLabel || null,
            freeText: ftTrim,
        };
    }

    if (id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
        const has = ftTrim.length > 0;
        return {
            categoryId: has ? SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID : null,
            categoryLabel: has ? (label || freeTextOptionLabel || null) : null,
            freeText: has ? ftTrim : null,
        };
    }

    const hasDiscrete = id !== '' && id !== SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID;
    return {
        categoryId: hasDiscrete ? id : null,
        categoryLabel: hasDiscrete ? (label || null) : null,
        freeText: null,
    };
}

/**
 * Whether exportData has any answered question.
 * @param {Record<string, { categoryId?: *, freeText?: * }>} answers
 */
export function hasAnySlideClassificationAnswer(answers) {
    return Object.values(answers || {}).some((a) => {
        const cid = a?.categoryId != null ? String(a.categoryId).trim() : '';
        const ft = a?.freeText != null ? String(a.freeText).trim() : '';
        return Boolean(cid) || Boolean(ft);
    });
}

/**
 * @param {object} question - normalized question
 * @param {{ categoryId?: string|null, categoryLabel?: string|null, freeText?: string|null }} answer
 */
export function isQuestionAnswerComplete(question, answer) {
    const a = answer ?? EMPTY_ANSWER;
    return isSlideLevelClassificationAnswerComplete({
        allowDiscreteCategories: question.allowDiscreteCategories,
        allowFreeText: question.allowFreeText,
        categories: question.categories ?? [],
        categoryId: a.categoryId ?? '',
        freeTextAnswer: a.freeText ?? '',
    });
}

/**
 * @param {object[]} questions - normalized
 * @param {Record<string, object>} answers
 */
export function isSlideLevelClassificationSlideComplete(questions, answers) {
    if (!Array.isArray(questions) || questions.length === 0) return false;
    return questions.every((q) => isQuestionAnswerComplete(q, answers?.[q.id]));
}

/**
 * @param {object[]} questions
 * @param {Record<string, object>} answers
 * @returns {string[]}
 */
export function getUnansweredQuestionLabels(questions, answers) {
    const labels = [];
    questions.forEach((q, i) => {
        if (!isQuestionAnswerComplete(q, answers?.[q.id])) {
            labels.push(getQuestionDisplayName(q, i));
        }
    });
    return labels;
}

/**
 * Count answered questions.
 * @param {object[]} questions
 * @param {Record<string, object>} answers
 */
export function countAnsweredQuestions(questions, answers) {
    let n = 0;
    questions.forEach((q) => {
        if (isQuestionAnswerComplete(q, answers?.[q.id])) n += 1;
    });
    return n;
}

/** Max selectable rows (categories + hybrid free-text) before using a dropdown control. */
export const SLC_MAX_INLINE_CHOICES = 5;

/**
 * Count of selectable answer rows (excludes Clear); hybrid includes free-text option.
 * @param {object} question - normalized question
 */
export function getQuestionSelectableChoiceCount(question) {
    if (!question?.allowDiscreteCategories) return 0;
    const categories = Array.isArray(question.categories) ? question.categories : [];
    let n = categories.length;
    if (question.allowDiscreteCategories && question.allowFreeText) {
        n += 1;
    }
    return n;
}

/**
 * @param {object} question - normalized question
 */
/**
 * Inline choice list is for QuestionsPanel cards only; TopBarControls passes layout="topBar".
 */
export function shouldUseInlineChoices(question) {
    if (!question?.allowDiscreteCategories) return false;
    return getQuestionSelectableChoiceCount(question) <= SLC_MAX_INLINE_CHOICES;
}

/**
 * First unanswered question index, or 0 when all complete (callers that show UI
 * for the featured question should treat answeredCount === total separately).
 * @param {object[]} questions
 * @param {Record<string, object>} answers
 */
export function getFeaturedQuestionIndex(questions, answers) {
    if (!Array.isArray(questions) || questions.length === 0) return 0;
    const idx = questions.findIndex(
        (q) => !isQuestionAnswerComplete(q, answers?.[q.id]),
    );
    return idx >= 0 ? idx : 0;
}

/**
 * Short summary of current answer for collapsed strip when using dropdown mode.
 * @param {object} question
 * @param {{ categoryId?: string, categoryLabel?: string, freeText?: string }|undefined} answer
 * @param {number} [qIndex]
 */
export function formatAnswerSummary(question, answer, qIndex = 0) {
    const a = answer ?? {};
    if (!isQuestionAnswerComplete(question, a)) {
        return 'Not answered';
    }
    const id = a.categoryId != null ? String(a.categoryId).trim() : '';
    if (id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
        const ft = String(a.freeText ?? '').trim();
        if (ft) return ft.length > 48 ? `${ft.slice(0, 47)}…` : ft;
        return getFreeTextOptionLabelForDisplay(question.freeText?.optionLabel);
    }
    const cl = String(a.categoryLabel ?? '').trim();
    if (cl) return cl;
    const cat = (question.categories ?? []).find((c) => String(c.id) === id);
    if (cat) return cat.label?.trim() ? cat.label : String(cat.id);
    return getQuestionDisplayName(question, qIndex);
}

/**
 * Normalizes free-text config for in-memory / form use. Do not trim optionLabel here — ConfigForm
 * reads normalized config as controlled values; trimming breaks typing (UI_CONFIG_MODEL_PRINCIPLES.md).
 * Trim only in {@link trimFreeTextConfigForPersist}.
 *
 * @param {unknown} ft
 * @returns {{ multiline: boolean, placeholder: string, optionLabel: string }}
 */
export function normalizeFreeTextSettings(ft) {
    const o = ft && typeof ft === 'object' ? ft : {};
    return {
        multiline: Boolean(o.multiline),
        placeholder: o.placeholder == null ? '' : String(o.placeholder),
        optionLabel: o.optionLabel == null ? '' : String(o.optionLabel),
    };
}

/**
 * Legacy configs omit allow* flags → discrete categories only.
 * @param {object} raw
 */
function normalizeAnswerModes(raw) {
    const hasD = Object.prototype.hasOwnProperty.call(raw, 'allowDiscreteCategories');
    const hasF = Object.prototype.hasOwnProperty.call(raw, 'allowFreeText');
    if (!hasD && !hasF) {
        return {
            allowDiscreteCategories: true,
            allowFreeText: false,
            freeText: normalizeFreeTextSettings({}),
        };
    }
    return {
        allowDiscreteCategories: raw.allowDiscreteCategories !== false,
        allowFreeText: Boolean(raw.allowFreeText),
        freeText: normalizeFreeTextSettings(raw.freeText),
    };
}

/**
 * Safe annotation UI config for runtime + publish validation.
 * @param {object} [config]
 */
export function normalizeSlideLevelClassificationConfig(config) {
    const raw = config && typeof config === 'object' ? config : {};

    const questions = migrateQuestionsFromRaw(raw);
    const meta = normalizeItemMetadataConfig(raw);

    const taskSectionLabelRaw = raw.taskSectionLabel;
    const taskSectionLabel = taskSectionLabelRaw == null
        ? DEFAULT_TASK_SECTION_LABEL
        : String(taskSectionLabelRaw);
    const sqLabelRaw = raw.singleQuestionControlLabel;
    const singleQuestionControlLabel = sqLabelRaw == null ? '' : String(sqLabelRaw);

    const out = {
        ...DEFAULT_SLIDE_LEVEL_CLASSIFICATION_CONFIG,
        ...raw,
        instructions: typeof raw.instructions === 'string' ? raw.instructions : '',
        taskSectionLabel,
        singleQuestionControlLabel,
        questions,
        itemMetadata: meta.itemMetadata,
        itemMetadataDisplayKeys: meta.itemMetadataDisplayKeys,
        columnLabels: meta.columnLabels,
        completionPolicy: normalizeCompletionPolicy(raw),
    };

    if (raw.roi && typeof raw.roi === 'object') {
        out.roi = raw.roi;
    }

    applyPrimaryQuestionCompat(out, questions[0]);

    return out;
}

/**
 * @param {object} config - should be normalized
 * @returns {{ code: string, message: string }[]}
 */
export function validateSlideLevelClassificationConfig(config) {
    const c = normalizeSlideLevelClassificationConfig(config);
    const issues = [];

    const questions = Array.isArray(c.questions) ? c.questions : [];

    if (questions.length < 1) {
        issues.push({
            code: 'no_questions',
            message: 'Add at least one question.',
        });
    }

    const im = c.itemMetadata ?? {};
    const rows = Array.isArray(im.rows) ? im.rows : [];
    const matchOpts = {
        trimWhitespace: im.trimWhitespace !== false,
        caseInsensitive: Boolean(im.caseInsensitive),
    };

    issues.push(...validateItemMetadataConfig(c));

    const seenQuestionIds = new Set();
    let anyDiscreteWithoutCategories = false;

    questions.forEach((q, qIdx) => {
        const qName = getQuestionDisplayName(q, qIdx);
        const qid = String(q.id ?? '').trim();

        if (!qid) {
            issues.push({
                code: 'empty_question_id',
                message: `${qName}: question id is required.`,
            });
        } else if (seenQuestionIds.has(qid)) {
            issues.push({
                code: 'duplicate_question_id',
                message: `${qName}: duplicate question id "${qid}".`,
            });
        } else {
            seenQuestionIds.add(qid);
        }

        const allowDisc = Boolean(q.allowDiscreteCategories);
        const allowFt = Boolean(q.allowFreeText);

        if (!allowDisc && !allowFt) {
            issues.push({
                code: 'no_answer_mode',
                message: `${qName}: choose at least one answer type (discrete categories and/or free-text).`,
            });
        }

        const categories = Array.isArray(q.categories) ? q.categories : [];

        if (allowDisc && categories.length === 0) {
            issues.push({
                code: 'missing_categories',
                message: `${qName}: discrete categories are enabled — add at least one category.`,
            });
            anyDiscreteWithoutCategories = true;
        }

        const seenCatIds = new Set();
        categories.forEach((cat, i) => {
            const rowNum = i + 1;
            const id = String(cat.id ?? '').trim();
            if (!id) {
                if (allowDisc) {
                    issues.push({
                        code: 'empty_category_id',
                        message: `${qName}, category row ${rowNum}: id is required.`,
                    });
                }
                return;
            }
            if (id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
                issues.push({
                    code: 'reserved_free_text_category_id',
                    message: `${qName}, category row ${rowNum}: id "${SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID}" is reserved for free-text answers.`,
                });
                return;
            }
            if (seenCatIds.has(id)) {
                issues.push({
                    code: 'duplicate_category_id',
                    message: `${qName}, category row ${rowNum}: duplicate id "${id}".`,
                });
            }
            seenCatIds.add(id);
        });
    });

    if (rows.length > 0 && anyDiscreteWithoutCategories) {
        issues.push({
            code: 'slide_rows_without_categories',
            message: 'Item metadata rows are configured but at least one question has discrete categories enabled with no categories defined.',
        });
    }

    return issues;
}

const PUBLISH_BLOCKING_CODES = new Set([
    'no_questions',
    'no_answer_mode',
    'empty_question_id',
    'duplicate_question_id',
    'empty_category_id',
    'duplicate_category_id',
    'reserved_free_text_category_id',
]);

/**
 * Reasons that should disable project Publish while in Draft.
 * @param {object} config - annotationUI.config (draft)
 * @returns {string[]}
 */
export function getSlideLevelClassificationPublishBlockingReasons(config) {
    const issues = validateSlideLevelClassificationConfig(config);
    return issues
        .filter((i) => PUBLISH_BLOCKING_CODES.has(i.code))
        .map((i) => i.message);
}
